import React, { useState, useEffect } from 'react';
import { FeedbackData } from '../../../types/chat.types';
import {
  FeedbackModal as FeedbackModal__Root,
  FeedbackModal__Dialog,
  FeedbackModal__Header,
  FeedbackModal__Title,
  FeedbackModal__CloseBtn,
  FeedbackModal__Content,
  FeedbackModal__FormGroup,
  FeedbackModal__Label,
  FeedbackModal__Description,
  FeedbackModal__Select,
  FeedbackModal__TextArea,
  FeedbackModal__Input,
  FeedbackModal__Footer,
  FeedbackModal__Btn,
  FeedbackModal__Star,
  FeedbackModal__StarRating,
} from './FeedbackModal.styles';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedbackData: FeedbackData) => void;
  initialReaction: 'thumbs-up' | 'thumbs-down';
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialReaction,
}) => {
  const [comment, setComment] = useState('');
  const [responseType, setResponseType] = useState<'answered' | 'couldnt-answer' | 'kept-asking' | ''>('');
  const [dataCorrectness, setDataCorrectness] = useState<'correct' | 'partly-correct' | 'wrong' | ''>('');
  const [overallRating, setOverallRating] = useState<number>(0);
  const [expectedAnswer, setExpectedAnswer] = useState('');

  const isNegativeFeedback = initialReaction === 'thumbs-down';

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setComment('');
      setResponseType('');
      setDataCorrectness('');
      setOverallRating(0);
      setExpectedAnswer('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    onSubmit({
      reaction: initialReaction,
      comment: comment.trim() || undefined,
      responseType: responseType || undefined,
      dataCorrectness: dataCorrectness || undefined,
      overallRating: overallRating > 0 ? overallRating : undefined,
      expectedAnswer: expectedAnswer.trim() || undefined,
    });
    onClose();
  };

  const renderStars = () => {
    return [1, 2, 3, 4, 5].map(star => (
      <FeedbackModal__Star
        key={star}
        type="button"
        $selected={star <= overallRating}
        onClick={() => setOverallRating(star)}
        aria-label={`${star} star${star > 1 ? 's' : ''}`}
      >
        ★
      </FeedbackModal__Star>
    ));
  };

  if (!isOpen) return null;

  return (
    <FeedbackModal__Root $isOpen={isOpen}>
      <FeedbackModal__Dialog onClick={e => e.stopPropagation()}>
        <FeedbackModal__Header>
          <FeedbackModal__Title>
            {isNegativeFeedback ? 'Help us improve' : 'Thank you for your feedback!'}
          </FeedbackModal__Title>
          <FeedbackModal__CloseBtn onClick={onClose} aria-label="Close">
            ×
          </FeedbackModal__CloseBtn>
        </FeedbackModal__Header>

        <FeedbackModal__Content>
          {isNegativeFeedback && (
            <>
              <FeedbackModal__FormGroup>
                <FeedbackModal__Label htmlFor="responseType">Response Type (Optional)</FeedbackModal__Label>
                <FeedbackModal__Description>How would you categorize the chatbot's response?</FeedbackModal__Description>
                <FeedbackModal__Select
                  id="responseType"
                  value={responseType}
                  onChange={e => setResponseType(e.target.value as 'answered' | 'couldnt-answer' | 'kept-asking' | '')}
                >
                  <option value="">Select an option...</option>
                  <option value="answered">Answered my question</option>
                  <option value="couldnt-answer">Couldn't answer</option>
                  <option value="kept-asking">Kept asking for clarification</option>
                </FeedbackModal__Select>
              </FeedbackModal__FormGroup>

              <FeedbackModal__FormGroup>
                <FeedbackModal__Label htmlFor="dataCorrectness">Data Correctness (Optional)</FeedbackModal__Label>
                <FeedbackModal__Description>Was the information provided correct?</FeedbackModal__Description>
                <FeedbackModal__Select
                  id="dataCorrectness"
                  value={dataCorrectness}
                  onChange={e => setDataCorrectness(e.target.value as 'correct' | 'partly-correct' | 'wrong' | '')}
                >
                  <option value="">Select an option...</option>
                  <option value="correct">Correct</option>
                  <option value="partly-correct">Partly correct</option>
                  <option value="wrong">Wrong</option>
                </FeedbackModal__Select>
              </FeedbackModal__FormGroup>

              <FeedbackModal__FormGroup>
                <FeedbackModal__Label>Overall Rating (Optional)</FeedbackModal__Label>
                <FeedbackModal__Description>Rate your overall experience (1-5 stars)</FeedbackModal__Description>
                <FeedbackModal__StarRating>{renderStars()}</FeedbackModal__StarRating>
              </FeedbackModal__FormGroup>

              <FeedbackModal__FormGroup>
                <FeedbackModal__Label htmlFor="expectedAnswer">Expected Answer (Optional)</FeedbackModal__Label>
                <FeedbackModal__Description>What answer were you expecting?</FeedbackModal__Description>
                <FeedbackModal__Input
                  id="expectedAnswer"
                  type="text"
                  placeholder="Describe what you expected..."
                  value={expectedAnswer}
                  onChange={e => setExpectedAnswer(e.target.value)}
                />
              </FeedbackModal__FormGroup>
            </>
          )}

          <FeedbackModal__FormGroup>
            <FeedbackModal__Label htmlFor="comment">
              {isNegativeFeedback ? 'Additional Comments (Optional)' : 'Comments (Optional)'}
            </FeedbackModal__Label>
            <FeedbackModal__Description>
              {isNegativeFeedback
                ? 'Tell us more about what went wrong...'
                : 'Tell us more about your experience...'
              }
            </FeedbackModal__Description>
            <FeedbackModal__TextArea
              id="comment"
              placeholder={isNegativeFeedback
                ? 'Please provide any additional details...'
                : 'What did you like about this response?'
              }
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </FeedbackModal__FormGroup>
        </FeedbackModal__Content>

        <FeedbackModal__Footer>
          <FeedbackModal__Btn onClick={onClose}>Cancel</FeedbackModal__Btn>
          <FeedbackModal__Btn $primary onClick={handleSubmit}>Submit</FeedbackModal__Btn>
        </FeedbackModal__Footer>
      </FeedbackModal__Dialog>
    </FeedbackModal__Root>
  );
};
